import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api';

const DAYS_SHORT = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

function mealDayLabel(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + 'T00:00:00');
  return `${DAYS_SHORT[d.getDay()]} ${d.getDate()}/${d.getMonth() + 1}`;
}

const PRICE_DB = {
  'poulet entier': 7.5, 'poulet':3.5, 'cuisses de poulet':4.5, 'blanc de poulet':5, 'dinde':5,
  'bœuf':6, 'viande hachée':5, 'steak':7, 'lardons':2, 'jambon':2.5, 'saucisse':3,
  'sardines':2, 'thon':2, 'saumon':6, 'cabillaud':6, 'merlu':5, 'lieu':4, 'poisson':5,
  'carottes':1.2, 'courgettes':1.5, 'aubergines':1.8, 'poireaux':1.5, 'pommes de terre':1.5,
  'tomates cerises':2.5, 'tomates pelées':1, 'tomates':2, 'oignons':1, 'ail':0.8,
  'poivrons':2, 'épinards':2, 'brocolis':1.8, 'champignons':2, 'haricots verts':1.5,
  'céleri':1.2, 'navets':1, 'panais':1.5, 'salade':1.2, 'concombre':1,
  'citron confit':1.5, 'citron':0.8, 'pommes':2, 'olives':2,
  'pâte brisée':1.5, 'pâte feuilletée':2, 'pâte à pizza':1.5,
  'lasagnes':2, 'gnocchi':2, 'macaronis':1.2, 'spaghetti':1.2,
  'pâtes':1.2, 'riz':1.5, 'semoule':1.2, 'farine':0.8, 'pain de mie':1.5,
  'pain pita':1.5, 'galette':1.5, 'pain':1.5,
  'lentilles':1.5, 'pois chiches':1.2, 'haricots blancs':1.2, 'chapelure':1,
  'crème fraîche':1.5, 'fromage frais':2, 'crème':1.5, 'lait de coco':1.5, 'lait':1.5,
  'emmental':2.5, 'gruyère':2.5, 'parmesan':3, 'mozzarella':2, 'comté':3, 'chèvre':2.5,
  'beurre':2, 'yaourt':2, 'oeuf':2.5, 'œufs':2.5,
  'sauce soja':2, 'moutarde':1.5, 'vinaigre':1, 'bouillon':1.5, 'huile':3,
  'maïs':1, 'curry':2, 'cumin':1.5, 'basilic':1.5, 'coriandre':1.5, 'aneth':1.5,
  'herbes':1, 'thym':1, 'persil':1, 'muscade':1.5, 'cannelle':1.5,
  'eau':2, 'jus':2, 'café':4, 'thé':3,
  'houmous':2, 'pesto':2.5, 'confiture':2.5,
};

function estimateItemPrice(name, quantity) {
  const n = name.toLowerCase();
  const keys = Object.keys(PRICE_DB).sort((a, b) => b.length - a.length);
  for (const key of keys) {
    if (n.includes(key)) {
      let price = PRICE_DB[key];
      if (quantity) {
        const qMatch = quantity.match(/(\d+(?:[.,]\d+)?)\s*(?:kg|kilo)/i);
        const lMatch = quantity.match(/(\d+(?:[.,]\d+)?)\s*(?:l|litre)/i);
        const uMatch = quantity.match(/^(\d+)$/);
        if (qMatch) price *= parseFloat(qMatch[1].replace(',', '.'));
        else if (lMatch) price *= parseFloat(lMatch[1].replace(',', '.'));
        else if (uMatch && parseInt(uMatch[1]) > 1) price *= Math.min(parseInt(uMatch[1]), 4);
      }
      return price;
    }
  }
  return 2.0;
}

const FOOD_CATS = ['Fruits & Légumes','Viandes & Poissons','Produits laitiers','Épicerie','Boulangerie','Boissons','Surgelés','Autre'];
const NONFOOD_CATS = ['Hygiène','Entretien','Pharmacie','Maison','Autre'];

export default function Shopping() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightIngredient = searchParams.get('ingredient');
  const highlightRef = useRef(null);
  const [highlighted, setHighlighted] = useState(null);
  const [items, setItems] = useState([]);
  const [foodForm, setFoodForm] = useState({ name: '', quantity: '', category: 'Autre' });
  const [nonFoodForm, setNonFoodForm] = useState({ name: '', quantity: '', notes: '', category: 'Autre' });
  const [addingFood, setAddingFood] = useState(false);
  const [foodCollapsed, setFoodCollapsed] = useState(true);
  const [nonFoodCollapsed, setNonFoodCollapsed] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', quantity: '', notes: '', category: 'Autre' });

  const [dislikeMsg, setDislikeMsg] = useState('');

  const load = useCallback(() => {
    api.get('/shopping').then(r => setItems(r.data)).catch(console.error);
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!highlightIngredient || !items.length) return;
    setFoodCollapsed(false);
    setHighlighted(highlightIngredient.toLowerCase());
    setTimeout(() => {
      highlightRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 300);
    setTimeout(() => {
      setHighlighted(null);
      setSearchParams({});
    }, 3000);
  }, [highlightIngredient, items]);

  const toggle = async (id) => { await api.post(`/shopping/${id}/toggle`); load(); };
  const del = async (id) => { await api.delete(`/shopping/${id}`); load(); };

  const dislikeItem = async (item) => {
    if (!window.confirm(`Marquer "${item.name}" comme ingrédient à éviter ?\nLes repas contenant cet ingrédient seront remplacés.`)) return;
    try {
      const { data } = await api.post(`/shopping/${item.id}/dislike`);
      load();
      const msg = data.affectedMeals > 0
        ? `👎 "${data.disliked}" exclu · ${data.affectedMeals} repas remplacés · 🛒 Courses mises à jour`
        : `👎 "${data.disliked}" ajouté aux ingrédients à éviter`;
      setDislikeMsg(msg);
      setTimeout(() => setDislikeMsg(''), 5000);
    } catch (e) { alert(e.response?.data?.error || e.message); }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditForm({ name: item.name, quantity: item.quantity || '', notes: item.notes || '', category: item.category || 'Autre' });
  };
  const saveEdit = async () => {
    await api.put(`/shopping/${editingId}`, editForm);
    setEditingId(null);
    load();
  };

  const clearOrphans = async () => {
    const orphans = items.filter(i => i.type === 'alimentaire' && !i.done && !i.meal_date && !i.meal_week);
    if (!orphans.length) return;
    if (!window.confirm(`Supprimer les ${orphans.length} articles alimentaires sans repas associé ?`)) return;
    await Promise.all(orphans.map(i => api.delete(`/shopping/${i.id}`)));
    load();
  };

  const clearDone = async (type) => {
    if (!window.confirm('Supprimer tous les articles cochés ?')) return;
    const done = items.filter(i => i.done && i.type === type);
    await Promise.all(done.map(i => api.delete(`/shopping/${i.id}`)));
    load();
  };

  const addFood = async () => {
    if (!foodForm.name.trim()) return;
    await api.post('/shopping', { ...foodForm, type: 'alimentaire' });
    setFoodForm({ name: '', quantity: '', category: 'Autre' });
    setAddingFood(false);
    load();
  };

  const addNonFood = async () => {
    if (!nonFoodForm.name.trim()) return;
    await api.post('/shopping', { ...nonFoodForm, type: 'non_alimentaire' });
    setNonFoodForm({ name: '', quantity: '', notes: '', category: 'Autre' });
    load();
  };

  const food = items.filter(i => i.type === 'alimentaire');
  const nonFood = items.filter(i => i.type === 'non_alimentaire');
  const foodPending = food.filter(i => !i.done);
  const foodOrphans = foodPending.filter(i => !i.meal_date && !i.meal_week);
  const nonFoodPending = nonFood.filter(i => !i.done);
  const foodDone = food.filter(i => i.done);
  const nonFoodDone = nonFood.filter(i => i.done);

  const foodTotal = foodPending.reduce((s, i) => s + estimateItemPrice(i.name, i.quantity), 0);

  const groupBy = (arr) => arr.reduce((acc, item) => {
    const cat = item.category || 'Autre';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  const foodGroups = groupBy(foodPending);
  const nonFoodGroups = groupBy(nonFoodPending);

  return (
    <div>
      <div className="topbar">
        <div className="page-title">🛒 Courses</div>
      </div>

      <div className="page" style={{ paddingTop: 0 }}>

        {dislikeMsg && (
          <div style={{ background: '#FFF8E1', color: '#E65100', borderRadius: 12, padding: '10px 16px', marginBottom: 12, fontSize: 13, fontWeight: 600 }}>
            {dislikeMsg}
          </div>
        )}

        {/* ===== ALIMENTAIRE ===== */}
        <div style={{ marginBottom: 24 }}>
          {/* Header section alimentaire */}
          <div onClick={() => setFoodCollapsed(c => !c)} style={{ background: 'linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)', borderRadius: 18, padding: '16px 18px', marginBottom: 12, cursor: 'pointer' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 22 }}>🥬</span>
                <div>
                  <div style={{ color: 'white', fontWeight: 800, fontSize: 16 }}>Alimentaire</div>
                  <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: 12 }}>{foodPending.length} article{foodPending.length !== 1 ? 's' : ''} restant{foodPending.length !== 1 ? 's' : ''}</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {foodOrphans.length > 0 && (
                  <button onClick={e => { e.stopPropagation(); clearOrphans(); }}
                    title="Supprimer les articles sans repas associé"
                    style={{ background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 8, color: 'white', fontSize: 11, fontWeight: 700, cursor: 'pointer', padding: '4px 8px', lineHeight: 1.4 }}>
                    ⚠️ {foodOrphans.length} sans repas
                  </button>
                )}
                {foodPending.length > 0 && (
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 22, fontWeight: 800, color: 'white' }}>~{foodTotal.toFixed(0)} €</div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)' }}>{Math.max(0, foodTotal * 0.8).toFixed(0)} – {(foodTotal * 1.2).toFixed(0)} €</div>
                  </div>
                )}
                <span style={{ fontSize: 20, color: 'white', transition: 'transform 0.2s', display: 'block', transform: foodCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}>▾</span>
              </div>
            </div>
          </div>

          {/* Contenu alimentaire (réductible) */}
          {!foodCollapsed && <>

          {/* Boutons action alimentaire */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <button className="btn btn-sm" onClick={() => setAddingFood(!addingFood)}
              style={{ flex: 1, background: addingFood ? '#E8F5E9' : '#F0FFF1', color: '#2E7D32', border: '1.5px solid #A5D6A7', fontWeight: 700 }}>
              + Ajouter un aliment
            </button>
            {foodDone.length > 0 && (
              <button className="btn btn-ghost btn-sm" onClick={() => clearDone('alimentaire')} style={{ color: '#EF5350' }}>
                🗑️ Cochés ({foodDone.length})
              </button>
            )}
          </div>

          {/* Formulaire ajout alimentaire */}
          {addingFood && (
            <div style={{ background: '#F1F8F1', borderRadius: 14, padding: 14, marginBottom: 10, border: '1.5px solid #C8E6C9' }}>
              <input className="form-input" placeholder="Nom du produit" value={foodForm.name}
                onChange={e => setFoodForm({ ...foodForm, name: e.target.value })}
                onKeyDown={e => e.key === 'Enter' && addFood()}
                style={{ marginBottom: 8 }} autoFocus />
              <div style={{ display: 'flex', gap: 8 }}>
                <input className="form-input" placeholder="Quantité (ex: 500g, 2)" value={foodForm.quantity}
                  onChange={e => setFoodForm({ ...foodForm, quantity: e.target.value })}
                  onKeyDown={e => e.key === 'Enter' && addFood()}
                  style={{ flex: 1 }} />
                <select className="form-input" value={foodForm.category} onChange={e => setFoodForm({ ...foodForm, category: e.target.value })} style={{ flex: 1 }}>
                  {FOOD_CATS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button className="btn btn-sm" onClick={() => setAddingFood(false)} style={{ flex: 1, background: '#eee', color: '#666' }}>Annuler</button>
                <button className="btn btn-primary btn-sm" onClick={addFood} style={{ flex: 2, background: '#4CAF50', border: 'none' }}>✓ Ajouter</button>
              </div>
            </div>
          )}

          {/* Liste alimentaire */}
          {foodPending.length === 0 && foodDone.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px 0', color: '#AAB', fontSize: 13 }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🥬</div>
              Liste alimentaire vide — sync depuis les repas ou ajoutez un article
            </div>
          ) : (
            <>
              {Object.entries(foodGroups).sort(([a], [b]) => a.localeCompare(b)).map(([cat, catItems]) => (
                <div key={cat} style={{ background: 'white', borderRadius: 14, padding: '10px 14px', marginBottom: 8, boxShadow: '0 1px 8px rgba(76,175,80,0.08)', borderLeft: '3px solid #81C784' }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: '#4CAF50', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>{cat}</div>
                  {catItems.map(item => {
                    const isHit = highlighted && item.name.toLowerCase().includes(highlighted);
                    return (
                    <div key={item.id} ref={isHit ? highlightRef : null}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '1px solid #F5F5F5', borderRadius: isHit ? 8 : 0, background: isHit ? '#FFF8C0' : 'transparent', transition: 'background 0.5s', marginLeft: isHit ? -4 : 0, paddingLeft: isHit ? 4 : 0 }}>
                      <button onClick={() => toggle(item.id)}
                        style={{ width: 24, height: 24, borderRadius: 6, border: '2px solid #81C784', background: item.done ? '#4CAF50' : 'white', color: 'white', fontWeight: 700, fontSize: 14, cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {item.done ? '✓' : ''}
                      </button>
                      <span style={{ flex: 1, fontSize: 14, textDecoration: item.done ? 'line-through' : 'none', color: item.done ? '#BBB' : '#1A1A2E' }}>{item.name}</span>
                      {item.quantity && <span style={{ fontSize: 12, color: '#8888AA', marginRight: 4 }}>{item.quantity}</span>}
                      {item.meal_date ? (
                        <button onClick={() => navigate(`/repas?date=${item.meal_date}`)} title="Voir le repas concerné"
                          style={{ background: '#EEF4FF', border: '1px solid #C5D8FF', borderRadius: 6, color: '#4A6FA5', fontSize: 11, fontWeight: 700, cursor: 'pointer', padding: '2px 6px', flexShrink: 0, lineHeight: 1.4 }}>
                          🍽️ {mealDayLabel(item.meal_date)}
                        </button>
                      ) : !item.meal_week && (
                        <span style={{ background: '#FFF8E1', border: '1px solid #FFD54F', borderRadius: 6, color: '#E65100', fontSize: 10, fontWeight: 700, padding: '2px 5px', flexShrink: 0, lineHeight: 1.4 }}>
                          ⚠️ sans repas
                        </span>
                      )}
                      <button onClick={() => dislikeItem(item)} title="Ne plus proposer cet ingrédient dans les repas"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, opacity: 0.4, padding: '0 2px', lineHeight: 1 }}>👎</button>
                      <button onClick={() => del(item.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#DDD', padding: '0 2px' }}>×</button>
                    </div>
                  );})}
                </div>
              ))}
              {foodDone.length > 0 && (
                <div style={{ background: '#F9FFF9', borderRadius: 14, padding: '10px 14px', marginBottom: 8, opacity: 0.7, border: '1px dashed #C8E6C9' }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: '#81C784', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>✓ Cochés ({foodDone.length})</div>
                  {foodDone.map(item => (
                    <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0' }}>
                      <button onClick={() => toggle(item.id)}
                        style={{ width: 24, height: 24, borderRadius: 6, border: '2px solid #81C784', background: '#4CAF50', color: 'white', fontWeight: 700, fontSize: 14, cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✓</button>
                      <span style={{ flex: 1, fontSize: 14, textDecoration: 'line-through', color: '#BBB' }}>{item.name}</span>
                      {item.quantity && <span style={{ fontSize: 12, color: '#CCC' }}>{item.quantity}</span>}
                      <button onClick={() => del(item.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#DDD' }}>×</button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          </>}
        </div>

        {/* Séparateur */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <div style={{ flex: 1, height: 1, background: 'linear-gradient(to right, #E0E4F0, transparent)' }} />
          <span style={{ fontSize: 11, fontWeight: 700, color: '#BBBBCC', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Non alimentaire</span>
          <div style={{ flex: 1, height: 1, background: 'linear-gradient(to left, #E0E4F0, transparent)' }} />
        </div>

        {/* ===== NON ALIMENTAIRE ===== */}
        <div>
          {/* Header section non alimentaire */}
          <div onClick={() => setNonFoodCollapsed(c => !c)} style={{ background: 'linear-gradient(135deg, #6C63FF 0%, #8B80FF 100%)', borderRadius: 18, padding: '16px 18px', marginBottom: 12, cursor: 'pointer' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 22 }}>🧴</span>
                <div>
                  <div style={{ color: 'white', fontWeight: 800, fontSize: 16 }}>Non alimentaire</div>
                  <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: 12 }}>{nonFoodPending.length} article{nonFoodPending.length !== 1 ? 's' : ''} restant{nonFoodPending.length !== 1 ? 's' : ''}</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {nonFoodDone.length > 0 && (
                  <button className="btn btn-ghost btn-sm" onClick={e => { e.stopPropagation(); clearDone('non_alimentaire'); }} style={{ color: 'rgba(255,255,255,0.8)', border: '1px solid rgba(255,255,255,0.3)' }}>
                    🗑️ Cochés
                  </button>
                )}
                <span style={{ fontSize: 20, color: 'white', transition: 'transform 0.2s', display: 'block', transform: nonFoodCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}>▾</span>
              </div>
            </div>
          </div>

          {/* Formulaire ajout direct non alimentaire */}
          {!nonFoodCollapsed && <>

          <div style={{ background: '#F0EEFF', borderRadius: 14, padding: 14, marginBottom: 12, border: '1.5px solid #C5C0FF' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#6C63FF', marginBottom: 8 }}>➕ Ajouter un article</div>
            <input className="form-input" placeholder="Ex: piles, lessive, dentifrice..." value={nonFoodForm.name}
              onChange={e => setNonFoodForm({ ...nonFoodForm, name: e.target.value })}
              onKeyDown={e => e.key === 'Enter' && addNonFood()}
              style={{ marginBottom: 8 }} />
            <div style={{ display: 'flex', gap: 8 }}>
              <input className="form-input" placeholder="Quantité (optionnel)" value={nonFoodForm.quantity}
                onChange={e => setNonFoodForm({ ...nonFoodForm, quantity: e.target.value })}
                onKeyDown={e => e.key === 'Enter' && addNonFood()}
                style={{ flex: 1 }} />
              <select className="form-input" value={nonFoodForm.category} onChange={e => setNonFoodForm({ ...nonFoodForm, category: e.target.value })} style={{ flex: 1 }}>
                {NONFOOD_CATS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <input className="form-input" placeholder="Infos / note (marque, magasin, lien...)" value={nonFoodForm.notes}
              onChange={e => setNonFoodForm({ ...nonFoodForm, notes: e.target.value })}
              onKeyDown={e => e.key === 'Enter' && addNonFood()}
              style={{ marginTop: 8 }} />
            <button className="btn btn-primary btn-sm" onClick={addNonFood} style={{ width: '100%', marginTop: 8, background: '#6C63FF', border: 'none' }}>
              ✓ Ajouter à la liste
            </button>
          </div>

          {/* Liste non alimentaire */}
          {nonFoodPending.length === 0 && nonFoodDone.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '16px 0', color: '#AAB', fontSize: 13 }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🧴</div>
              Aucun article non alimentaire
            </div>
          ) : (
            <>
              {Object.entries(nonFoodGroups).sort(([a], [b]) => a.localeCompare(b)).map(([cat, catItems]) => (
                <div key={cat} style={{ background: 'white', borderRadius: 14, padding: '10px 14px', marginBottom: 8, boxShadow: '0 1px 8px rgba(108,99,255,0.08)', borderLeft: '3px solid #9C94FF' }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: '#6C63FF', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>{cat}</div>
                  {catItems.map(item => (
                    <div key={item.id} style={{ padding: '7px 0', borderBottom: '1px solid #F5F5F5' }}>
                      {editingId === item.id ? (
                        <div style={{ background: '#F0EEFF', borderRadius: 10, padding: 10, border: '1.5px solid #C5C0FF' }}>
                          <input className="form-input" value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} style={{ marginBottom: 6 }} autoFocus />
                          <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                            <input className="form-input" placeholder="Quantité" value={editForm.quantity} onChange={e => setEditForm({ ...editForm, quantity: e.target.value })} style={{ flex: 1 }} />
                            <select className="form-input" value={editForm.category} onChange={e => setEditForm({ ...editForm, category: e.target.value })} style={{ flex: 1 }}>
                              {NONFOOD_CATS.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                          </div>
                          <input className="form-input" placeholder="Infos / note (marque, magasin, lien...)" value={editForm.notes} onChange={e => setEditForm({ ...editForm, notes: e.target.value })} onKeyDown={e => e.key === 'Enter' && saveEdit()} style={{ marginBottom: 8 }} />
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button className="btn btn-sm" onClick={() => setEditingId(null)} style={{ flex: 1, background: '#eee', color: '#666' }}>Annuler</button>
                            <button className="btn btn-primary btn-sm" onClick={saveEdit} style={{ flex: 2, background: '#6C63FF', border: 'none' }}>✓ Enregistrer</button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <button onClick={() => toggle(item.id)}
                            style={{ width: 24, height: 24, borderRadius: 6, border: '2px solid #9C94FF', background: item.done ? '#6C63FF' : 'white', color: 'white', fontWeight: 700, fontSize: 14, cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {item.done ? '✓' : ''}
                          </button>
                          <div style={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => startEdit(item)}>
                            <div style={{ fontSize: 14, color: '#1A1A2E' }}>{item.name}</div>
                            {item.notes && <div style={{ fontSize: 11, color: '#8888AA', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>💬 {item.notes}</div>}
                          </div>
                          {item.quantity && <span style={{ fontSize: 12, color: '#8888AA', marginRight: 4, flexShrink: 0 }}>{item.quantity}</span>}
                          <button onClick={() => startEdit(item)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#C5C0FF', padding: '0 2px' }}>✏️</button>
                          <button onClick={() => del(item.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#DDD', padding: '0 2px' }}>×</button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ))}
              {nonFoodDone.length > 0 && (
                <div style={{ background: '#F5F3FF', borderRadius: 14, padding: '10px 14px', marginBottom: 8, opacity: 0.7, border: '1px dashed #C5C0FF' }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: '#9C94FF', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>✓ Cochés ({nonFoodDone.length})</div>
                  {nonFoodDone.map(item => (
                    <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0' }}>
                      <button onClick={() => toggle(item.id)}
                        style={{ width: 24, height: 24, borderRadius: 6, border: '2px solid #9C94FF', background: '#6C63FF', color: 'white', fontWeight: 700, fontSize: 14, cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✓</button>
                      <span style={{ flex: 1, fontSize: 14, textDecoration: 'line-through', color: '#BBB' }}>{item.name}</span>
                      {item.quantity && <span style={{ fontSize: 12, color: '#CCC' }}>{item.quantity}</span>}
                      <button onClick={() => del(item.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: '#DDD' }}>×</button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          </>}
        </div>
      </div>
    </div>
  );
}
